<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Leaves</span>
        <h1 class="hris-page-title">Employee Leave Applications</h1>
        <p class="hris-page-subtitle">Review and approve or decline pending leave applications.</p>
      </div>

      <div
        v-if="selectedApplicationIds.length > 0"
        class="hris-toolbar"
        style="justify-content: flex-end;"
      >
        <span class="hris-badge hris-badge-accent">
          {{ selectedApplicationIds.length }}
          {{
            selectedApplicationIds.length === 1 ? "application" : "applications"
          }}
          selected
        </span>
        <q-btn
          unelevated
          class="hris-q-primary"
          icon="fas fa-check"
          label="Approve selected"
          no-caps
          @click="openBulkConfirm('Approve')"
        ></q-btn>
        <q-btn
          unelevated
          class="hris-q-outline"
          icon="fas fa-times"
          label="Decline selected"
          no-caps
          @click="openBulkConfirm('Decline')"
        ></q-btn>
        <q-btn flat dense class="hris-q-ghost" label="Clear selection" no-caps @click="clearSelection"></q-btn>
      </div>

      <div
        v-for="sec in tableSections"
        :key="sec.leaveTypeKey"
        class="hris-panel"
      >
        <div class="hris-panel-title">{{ sec.title }}</div>
        <q-table
          flat
          :data="sec.rows"
          :columns="columnsWithSelect"
          :pagination="getPagination(sec.leaveTypeKey)"
          @update:pagination="val => updatePagination(getPagination(sec.leaveTypeKey), val)"
          row-key="id"
          dense
          binary-state-sort
        >
          <q-tr
            slot="body"
            slot-scope="props"
            :props="props"
            class="cursor-pointer"
            @click="viewDetails(props.row.type || sec.leaveTypeKey, props.row.id)"
          >
            <q-td key="select" :props="props" @click.native.stop>
              <div class="flex flex-center">
                <q-checkbox
                  v-if="isRowPendingSelectable(props.row)"
                  dense
                  :value="
                    selectedApplicationIds.indexOf(
                      Number(props.row.id)
                    ) !== -1
                  "
                  color="green"
                  @input="
                    toggleRowSelection(Number(props.row.id), $event)
                  "
                ></q-checkbox>
                <span v-else class="text-grey-6">—</span>
              </div>
            </q-td>
            <q-td key="status" :props="props">{{
              props.row.status
            }}</q-td>
            <q-td key="date_applied" :props="props">{{
              props.row.date_applied
            }}</q-td>
            <q-td key="inclusive_dates" :props="props">{{
              props.row.inclusive_dates
            }}</q-td>
            <q-td key="name" :props="props">{{ props.row.name }}</q-td>
            <q-td key="department" :props="props">{{
              props.row.department
            }}</q-td>
          </q-tr>
          <template v-slot:no-data>
            <div class="hris-empty full-width">No Applications Yet</div>
          </template>
        </q-table>
      </div>
    </div>

    <q-dialog v-model="loadingModel" persistent>
      <div class="hris-loading" style="background: transparent; min-width: 200px;">
        <q-spinner color="green" size="3em"></q-spinner>
        <div>{{ loadingText }}</div>
      </div>
    </q-dialog>

    <q-dialog
      v-model="leaveDetailsDialog"
      persistent
      transition-show="scale"
      transition-hide="scale"
    >
      <div class="hris-modal hris-modal-lg">
        <div class="hris-modal-header">
          <div>
            <h2 class="hris-modal-title">Leave Application Details</h2>
            <p class="hris-modal-subtitle">Leave Type: {{ leaveType }}</p>
          </div>
          <q-btn flat dense round v-close-popup aria-label="Close">
            <i class="fas fa-times"></i>
          </q-btn>
        </div>
        <div class="hris-modal-body">
          <div class="hris-form-row">
            <div class="hris-form-field" style="flex-basis: 100%;">
              <q-input
                :value="viewLeaveDetails.status"
                label="Status"
                outlined
                readonly
                stack-label
                dense
                color="green"
              ></q-input>
            </div>
          </div>
          <div class="hris-form-row">
            <div class="hris-form-field" style="flex-basis: 100%;">
              <q-input
                :value="viewLeaveDetails.employee_name"
                label="Employee Name"
                outlined
                readonly
                stack-label
                dense
                color="green"
              ></q-input>
            </div>
          </div>
          <div class="hris-form-row">
            <div class="hris-form-field" style="flex-basis: 100%;">
              <q-input
                :value="viewLeaveDetails.employee_department"
                label="Department"
                outlined
                readonly
                stack-label
                dense
                color="green"
              ></q-input>
            </div>
          </div>
          <div class="hris-form-row">
            <div class="hris-form-field">
              <q-input
                :value="viewLeaveDetails.date_applied"
                label="Date Applied"
                outlined
                readonly
                stack-label
                dense
                color="green"
              ></q-input>
            </div>
            <div class="hris-form-field">
              <q-input
                :value="viewLeaveDetails.total_days"
                label="Total Days"
                outlined
                readonly
                stack-label
                dense
                color="green"
              ></q-input>
            </div>
          </div>
          <div class="hris-form-row">
            <div class="hris-form-field">
              <q-input
                :value="viewLeaveDetails.date_start"
                label="Date Start"
                outlined
                readonly
                stack-label
                dense
                color="green"
              ></q-input>
            </div>
            <div class="hris-form-field">
              <q-input
                :value="viewLeaveDetails.date_end"
                label="Date End"
                outlined
                readonly
                stack-label
                dense
                color="green"
              ></q-input>
            </div>
          </div>
          <div class="hris-form-row">
            <div class="hris-form-field" style="flex-basis: 100%;">
              <q-input
                :value="viewLeaveDetails.reason"
                label="Reason"
                type="textarea"
                outlined
                readonly
                stack-label
                dense
                color="green"
              ></q-input>
            </div>
          </div>
          <template v-if="viewLeaveDetails.status != 'Pending'">
            <div class="hris-form-row">
              <div class="hris-form-field">
                <q-input
                  :value="viewLeaveDetails.approver"
                  label="Processed By"
                  outlined
                  readonly
                  stack-label
                  dense
                  color="green"
                ></q-input>
              </div>
              <div class="hris-form-field">
                <q-input
                  :value="viewLeaveDetails.date_processed"
                  label="Date Processed"
                  outlined
                  readonly
                  stack-label
                  dense
                  color="green"
                ></q-input>
              </div>
            </div>
            <div class="hris-form-row">
              <div class="hris-form-field" style="flex-basis: 100%;">
                <q-input
                  :value="viewLeaveDetails.approver_remarks"
                  label="Remarks"
                  type="textarea"
                  outlined
                  readonly
                  stack-label
                  dense
                  color="green"
                ></q-input>
              </div>
            </div>
          </template>
          <div class="hris-form-row" v-if="viewLeaveDetails.status == 'Pending'">
            <div class="hris-form-field" style="flex-basis: 100%;">
              <q-input
                v-model="form.approverRemarks"
                label="Approver Remarks"
                type="textarea"
                outlined
                stack-label
                dense
                color="green"
              ></q-input>
            </div>
          </div>
        </div>
        <div class="hris-modal-footer">
          <q-btn
            v-if="viewLeaveDetails.status == 'Pending'"
            unelevated
            class="hris-q-primary"
            @click="processApplication('Approve')"
            label="Approve"
            no-caps
          ></q-btn>
          <q-btn
            v-if="viewLeaveDetails.status == 'Pending'"
            unelevated
            class="hris-q-outline"
            @click="processApplication('Decline')"
            label="Decline"
            no-caps
          ></q-btn>
          <q-btn unelevated class="hris-q-ghost" label="Close" v-close-popup no-caps></q-btn>
        </div>
      </div>
    </q-dialog>

    <!-- Bulk Confirmation Dialog -->
    <q-dialog v-model="bulkConfirmDialog" persistent>
      <div class="hris-modal">
        <div class="hris-modal-header">
          <div>
            <h2 class="hris-modal-title">{{ bulkAction }} {{ selectedApplicationIds.length }} applications</h2>
          </div>
        </div>
        <div class="hris-modal-body">
          <q-input
            v-model="bulkRemarks"
            type="textarea"
            outlined
            label="Optional Remarks"
            dense
            color="green"
            autogrow
          ></q-input>
        </div>
        <div class="hris-modal-footer">
          <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup no-caps></q-btn>
          <q-btn
            unelevated
            class="hris-q-primary"
            :label="'Confirm ' + bulkAction"
            @click="submitBulkProcess"
            :loading="bulkPendingSubmit"
            no-caps
          ></q-btn>
        </div>
      </div>
    </q-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { date } from "quasar";

export default {
  name: "MyApprovals_Leave",
  data() {
    return {
      is_pto_enabled: false,
      loadingModel: false,
      loadingText: "",
      leaveDetailsDialog: false,
      bulkConfirmDialog: false,
      bulkAction: "",
      bulkRemarks: "",
      elAppResultData: [],
      vlAppResultData: [],
      slAppResultData: [],
      mlAppResultData: [],
      plAppResultData: [],
      mcAppResultData: [],
      spAppResultData: [],
      elPagination: { page: 1, rowsPerPage: 5 },
      vlPagination: { page: 1, rowsPerPage: 5 },
      slPagination: { page: 1, rowsPerPage: 5 },
      mlPagination: { page: 1, rowsPerPage: 5 },
      plPagination: { page: 1, rowsPerPage: 5 },
      mcPagination: { page: 1, rowsPerPage: 5 },
      spPagination: { page: 1, rowsPerPage: 5 },
      unifiedPagination: { page: 1, rowsPerPage: 10 },
      ResultColumns: [
        { name: "status", field: "status", label: "Status", align: "center" },
        {
          name: "date_applied",
          field: "date_applied",
          label: "Date Applied",
          sortable: true,
          style: "width: 180px",
          align: "center",
          headerStyle: "width: 180px",
        },
        {
          name: "inclusive_dates",
          field: "inclusive_dates",
          label: "Inclusive Date(s)",
          style: "width: 270px",
          align: "center",
          headerStyle: "width: 270px",
        },
        {
          name: "name",
          field: "name",
          label: "Name",
          align: "center",
        },
        {
          name: "department",
          field: "department",
          label: "Department",
          style: "width: 250px",
          align: "center",
          headerStyle: "width: 250px",
        },
      ],
      leaveType: "",
      viewLeaveDetails: {},
      form: {
        approverAction: "",
        approverRemarks: "",
        appType: "",
        appID: "",
      },
      selectedApplicationIds: [],
      bulkPendingSubmit: false,
    };
  },
  computed: {
    hasUnifiedLeaveFeature: function() {
      return (this.userData.features || []).includes('pto');
    },
    columnsWithSelect() {
      return [
        {
          name: "select",
          align: "center",
          label: "",
          field: "__select",
          sortable: false,
          style: "width: 52px",
          headerStyle: "width: 52px",
        },
        ...this.ResultColumns,
      ];
    },
    tableSections() {
      if (this.is_pto_enabled) {
        return [
          {
            leaveTypeKey: "paidLeave",
            title: "Leave Applications",
            rows: [
              ...this.elAppResultData.map(r => ({...r, type: 'elApp'})),
              ...this.vlAppResultData.map(r => ({...r, type: 'vlApp'})),
              ...this.slAppResultData.map(r => ({...r, type: 'slApp'}))
            ],
            pagination: this.unifiedPagination
          },
          {
            leaveTypeKey: "mlApp",
            title: "Maternity Leave",
            rows: this.mlAppResultData,
            pagination: this.mlPagination
          },
          {
            leaveTypeKey: "plApp",
            title: "Paternity Leave",
            rows: this.plAppResultData,
            pagination: this.plPagination
          },
          {
            leaveTypeKey: "mcApp",
            title: "Magna Carta Leave",
            rows: this.mcAppResultData,
            pagination: this.mcPagination
          },
          {
            leaveTypeKey: "spApp",
            title: "Solo Parent Leave",
            rows: this.spAppResultData,
            pagination: this.spPagination
          },
        ];
      }
      return [
        { leaveTypeKey: "elApp", title: "Emergency Leave", rows: this.elAppResultData, pagination: this.elPagination },
        { leaveTypeKey: "vlApp", title: "Vacation Leave", rows: this.vlAppResultData, pagination: this.vlPagination },
        { leaveTypeKey: "slApp", title: "Sick Leave", rows: this.slAppResultData, pagination: this.slPagination },
        { leaveTypeKey: "mlApp", title: "Maternity Leave", rows: this.mlAppResultData, pagination: this.mlPagination },
        { leaveTypeKey: "plApp", title: "Paternity Leave", rows: this.plAppResultData, pagination: this.plPagination },
        { leaveTypeKey: "mcApp", title: "Magna Carta Leave", rows: this.mcAppResultData, pagination: this.mcPagination },
        { leaveTypeKey: "spApp", title: "Solo Parent Leave", rows: this.spAppResultData, pagination: this.spPagination },
      ];
    },
  },
  methods: {
    getPagination(key) {
      const map = {
        'elApp': this.elPagination,
        'vlApp': this.vlPagination,
        'slApp': this.slPagination,
        'mlApp': this.mlPagination,
        'plApp': this.plPagination,
        'mcApp': this.mcPagination,
        'spApp': this.spPagination,
        'paidLeave': this.unifiedPagination
      };
      return map[key] || this.elPagination;
    },
    updatePagination(paginationObj, newPagination) {
        Object.assign(paginationObj, newPagination);
    },
    loadStatus: function() {
      this.is_pto_enabled = this.hasUnifiedLeaveFeature;
    },
    loadApproverList: function () {
      this.loadingModel = true;
      this.loadingText = "Retrieving Applications";
      axios
        .create({
          baseURL: this.apiUrl,
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        })
        .get("/leave/applications/retrieve-approver-list")
        .then((res) => {
          const applications = res.data.data;
          Object.keys(applications).forEach((key) => {
            const appKey = key + "ResultData";
            this[appKey] = [];
            for (let eachApp of applications[key]) {
              let tempDate = date.extractDate(
                eachApp.date_applied,
                "YYYY-MM-DD HH:mm:ss"
              );
              let date_applied = date.formatDate(tempDate, "MMMM DD, YYYY");

              let tempIncDate = eachApp.inclusive_dates.split("/");
              let finalIncDate = "";
              if (tempIncDate[0] == tempIncDate[1]) {
                finalIncDate = date.formatDate(tempIncDate[0], "MMMM DD, YYYY");
              } else {
                let tempInc1 = date.formatDate(tempIncDate[0], "MMMM DD");
                let tempInc2 = date.formatDate(tempIncDate[1], "DD, YYYY");
                finalIncDate = tempInc1 + " - " + tempInc2;
              }

              this[appKey].push({
                id: eachApp.id,
                status: eachApp.status,
                date_applied: date_applied,
                inclusive_dates: finalIncDate,
                name: eachApp.name,
                department: eachApp.department,
              });
            }
          });
        })
        .finally(() => {
          this.loadingModel = false;
        });
    },
    viewDetails: function (leaveApp, rowID) {
      this.loadingModel = true;
      this.loadingText = "Retrieving Details";
      axios
        .create({
          baseURL: this.apiUrl,
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        })
        .get("/leave/applications/details", {
          params: {
            id: rowID,
            type: leaveApp,
          },
        })
        .then((res) => {
          this.loadingModel = false;
          this.viewLeaveDetails = res.data.data;
          this.leaveDetailsDialog = true;
          this.form.appID = rowID;
          this.form.appType = leaveApp;
          this.form.approverRemarks = this.viewLeaveDetails.approver_remarks || "";

          let d = date.extractDate(
            this.viewLeaveDetails.date_applied,
            "YYYY-MM-DD HH:mm:ss"
          );
          this.viewLeaveDetails.date_applied = date.formatDate(
            d,
            "MMMM DD, YYYY @ hh:mmA"
          );
          this.viewLeaveDetails.date_start = date.formatDate(
            this.viewLeaveDetails.date_start,
            "MMMM DD, YYYY"
          );
          this.viewLeaveDetails.date_end = date.formatDate(
            this.viewLeaveDetails.date_end,
            "MMMM DD, YYYY"
          );

          if (this.viewLeaveDetails.status != "Pending") {
            let dp = date.extractDate(
              this.viewLeaveDetails.date_processed,
              "YYYY-MM-DD HH:mm:ss"
            );
            this.viewLeaveDetails.date_processed = date.formatDate(
              dp,
              "MMMM DD, YYYY @ hh:mmA"
            );
          }

          const types = {
            elApp: "Emergency Leave",
            vlApp: "Vacation Leave",
            slApp: "Sick Leave",
            mlApp: "Maternity Leave",
            plApp: "Paternity Leave",
            mcApp: "Magna Carta Leave",
            spApp: "Solo Parent Leave",
          };
          this.leaveType = types[leaveApp] || "Unknown";
        });
    },
    processApplication: function (action) {
      this.form.approverAction = action;
      this.loadingModel = true;
      this.loadingText = action === "Approve" ? "Approving" : "Declining";

      let formData = new FormData();
      formData.append("form_data", JSON.stringify(this.form));

      axios
        .create({
          baseURL: this.apiUrl,
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        })
        .post("/leave/applications/approver-process", formData)
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({
              color: action === "Approve" ? "green-7" : "red-7",
              message: "Application " + action + "d successfully",
            });
            this.leaveDetailsDialog = false;
            this.loadApproverList();
          }
        })
        .finally(() => {
          this.loadingModel = false;
        });
    },
    isRowPendingSelectable(row) {
      return row.status === "Pending" || row.status === "Approved" || row.status === "Processing" || row.status === "Processed";
    },
    toggleRowSelection(id, val) {
      const index = this.selectedApplicationIds.indexOf(id);
      if (val) {
        if (index === -1) this.selectedApplicationIds.push(id);
      } else {
        if (index !== -1) this.selectedApplicationIds.splice(index, 1);
      }
    },
    clearSelection() {
      this.selectedApplicationIds = [];
    },
    openBulkConfirm(action) {
      this.bulkAction = action;
      this.bulkRemarks = "";
      this.bulkConfirmDialog = true;
    },
    submitBulkProcess() {
      this.bulkPendingSubmit = true;
      const payload = {
        approverAction: this.bulkAction,
        approverRemarks: this.bulkRemarks,
        appIDs: this.selectedApplicationIds,
      };

      let formData = new FormData();
      formData.append("form_data", JSON.stringify(payload));

      axios
        .create({
          baseURL: this.apiUrl,
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        })
        .post("/leave/applications/approver-process", formData)
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({
              color: "positive",
              message: res.data.message,
            });
            this.bulkConfirmDialog = false;
            this.clearSelection();
            this.loadApproverList();
          }
        })
        .finally(() => {
          this.bulkPendingSubmit = false;
        });
    },
  },
  mounted() {
    this.loadStatus();
    this.loadApproverList();
  },
};
</script>

<style lang="css" scoped>
</style>
